import { FC, useContext, useEffect, useCallback } from "react";
import { Typography } from "@mui/material";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useDeletePosition from "src/Pages/Company/OrganizationStructure/api/useDeletePosition";
import __ from "src/utils/Translations";
import { Position } from "src/Pages/Company/OrganizationStructure/Positions/positions.models";

interface DeletePositionModalProps {
  companyId: string;
  structureId: string;
  position: Position;
  onClose: () => void;
}

const DeletePositionModal: FC<DeletePositionModalProps> = ({
  companyId,
  structureId,
  position,
  onClose,
}) => {
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const { mutate: deletePosition, isPending } = useDeletePosition(
    companyId,
    structureId
  );

  const { employeesNumber } = position;

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [resetModal, onClose]);

  const handleDelete = useCallback(() => {
    deletePosition(position.id, { onSuccess: handleClose });
  }, [deletePosition, position.id, handleClose]);

  useEffect(() => {
    setModal({
      open: true,
      title: __("Usunąć stanowisko?"),
      maxWidth: 628,
      disableExitButton: true,
      disableOutsideClick: true,
      customElements: (
        <>
          <Typography sx={{ mt: 2 }}>
            {__("Stanowisko ")}
            <span style={{ fontWeight: 600 }}>„{position.value}"</span>
            {__(" zostanie trwale usunięte z systemu.")}
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {__("Tej operacji nie można cofnąć.")}
          </Typography>
          {employeesNumber > 0 && (
            <Typography sx={{ mt: 2 }}>
              {__("To stanowisko jest obecnie ")}
              <span style={{ fontWeight: 600 }}>
                {__("przypisane do {0} {1}", [
                  String(employeesNumber),
                  employeesNumber === 1 ? __("pracownika") : __("pracowników"),
                ])}
              </span>
              .<br />
              {__(
                "Po usunięciu stracą je i będą wymagać ponownego przypisania."
              )}
            </Typography>
          )}
        </>
      ),
      actions: [
        {
          label: __("Anuluj"),
          variant: "outlined" as const,
          callbackFn: handleClose,
        },
        {
          label: __("Usuń"),
          variant: "contained" as const,
          color: "error" as const,
          callbackFn: handleDelete,
          loading: isPending,
        },
      ],
    });
  }, [setModal, handleClose, handleDelete, isPending, position.value, employeesNumber]);

  useEffect(
    () => () => {
      resetModal();
    },
    [resetModal]
  );

  return null;
};

export default DeletePositionModal;
